import React from 'react';

import { useIntl } from 'react-intl';

import { useCreateValidation } from 'src/features/validations/api/useCreateValidation';
import ValidationFormModal from 'src/features/validations/components/forms/ValidationFormModal';
import { validationMessages } from 'src/features/validations/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import { queryClient } from 'src/lib/react-query';
import { LIST_ACTIVE_STAY_PERIODS_RESOURCE } from 'src/features/stay/api/useListActiveStayPeriods';
import { ACTIVE_STAY_RESOURCE } from 'src/features/stay/api/useGetActiveStay';
import { ActiveStay, ActiveStayPeriod } from 'src/features/stay/types';

interface ValidationFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedStayPeriod?: ActiveStayPeriod;
    selectedStay?: ActiveStay;
    children?: React.ReactNode;
}

export default function CreateValidationFormModal<TData>({
    className,
    children,
    selectedStay,
    selectedStayPeriod,
}: ValidationFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate, isPending } = useCreateValidation({
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [LIST_ACTIVE_STAY_PERIODS_RESOURCE] });
            queryClient.invalidateQueries({ queryKey: [ACTIVE_STAY_RESOURCE] });

            toast({
                title: formatMessage(validationMessages.stayValidated),
                description: formatMessage(validationMessages.stayValidatedDescription),
            });
        },
    });

    return (
        <ValidationFormModal
            selectedStayPeriod={selectedStayPeriod}
            selectedStay={selectedStay}
            className={className}
            mutate={(data, onSuccess, onError) => mutate({ parkingId, ...data }, { onSuccess, onError })}
            title={formatMessage(validationMessages.validate)}
            icon={ICON_MAP.TICKET_PERFORATED}
            isPending={isPending}
            subtitle={formatMessage(validationMessages.validateStay)}
        >
            {children}
        </ValidationFormModal>
    );
}
